// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A RECOMENDAÇÃO NO DIA LIVRE — no painel, onde a pergunta se faz (D-087)
// ============================================================================
//
// 📗 Cliente, 2026-08-11: *«nos detalhes do intervalo, nos dias livres, não fazia sentido aparecer lá
//    uma recomendação? Da melhor solução? Dependendo do contexto, próxima viagem etc.»*
//
// ⚠️⚠️ **E É MELHOR DO QUE A LISTA QUE EU TINHA FEITO.** 📌 A família na fila diz «5 682 intervalos
//    discordam» — verdade que ninguém usa. Aqui responde-se onde a pergunta nasce: o planeador clicou
//    naquele dia porque quer saber o que fazer com aqueles dias. É a mesma decisão do «porque é que
//    esta opção ficou em 7.º», que se responde na linha e não num separador.
//
// ⚠️ **Só carrega quando o dia é livre**, e só quando o painel está aberto. Um pedido por célula
//    percorrida seriam centenas por minuto.
//
// ⚠️⚠️ **E NÃO HÁ BOTÃO PARA APLICAR, de propósito.** 📌 Recomendar é dizer o que a conta diz; aplicar
//    era escrever nos dias de um motorista com base em dois preços inventados. Enquanto D-056 e D-059
//    forem `placeholder`, um botão «aplicar» aqui seria a coisa mais perigosa deste ecrã — e a
//    recomendação diz-lhe isso na cara.
// ============================================================================

import { useEffect, useState } from 'react';

interface Decisao {
  readonly motoristaId: string;
  readonly noites: number;
  readonly recomendado: 'em_casa' | 'a_espera' | null;
  readonly meio: string;
  readonly chaveDoMeio: string;
  readonly comparacao: {
    readonly esperar: { readonly custo: number };
    readonly irACasa: { readonly custo: number };
    readonly margem: number;
    readonly confianca: 'palpite' | 'medido';
    readonly ressalvas: readonly string[];
  };
}

interface Resposta {
  readonly dia0: number;
  readonly dia1: number;
  readonly noites: number;
  readonly localidade: string | null;
  readonly pais: string | null;
  readonly servicoAnterior: { readonly dia: number; readonly referencia: string | null } | null;
  readonly servicoSeguinte: {
    readonly dia: number;
    readonly referencia: string | null;
    readonly localidade: string | null;
  } | null;
  readonly condutores: readonly { readonly id: string; readonly nome: string }[];
  readonly decisoes: readonly Decisao[];
  readonly semConta: string | null;
  readonly trocaAMeio: number;
  /**
   * ⚠️⚠️ **O QUE JÁ ESTÁ REGISTADO SOBRE ESTE DIA — e é a razão desta secção mudar hoje.**
   *
   * 📗 Cliente, 2026-08-12: *«eu mesmo pondo que o motorista vai a casa… continua a aparecer
   * pernoita fora da base, alojamento pago»*. 📌 A decisão gravava-se na linha **dele**
   * (`viatura_id NULL`, D-087) e o painel é uma vista de **viatura-dia**: a escrita era invisível
   * **por construção**, e o planeador não tinha como saber que tinha acontecido.
   */
  readonly pernoitaRegistada?: readonly {
    readonly motoristaId: string;
    readonly nome: string;
    readonly tipoDia: string;
    readonly localidade: string | null;
  }[];
}

export function RecomendacaoDoIntervalo({
  viaturaId,
  dia,
  nomeLocalidade,
  nomeDoDia,
}: {
  readonly viaturaId: string;
  readonly dia: number;
  /** O nome completo de um código de localidade — o painel já o sabe fazer. */
  readonly nomeLocalidade: (codigo: string) => string;
  /** `dd/mm` de um índice do ano, para as datas se lerem. */
  readonly nomeDoDia: (dia: number) => string;
}) {
  const [d, definirD] = useState<Resposta | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  /**
   * O detalhe abre a PEDIDO.
   *
   * WWWW 📗 Cliente, 2026-08-11: *«nao quero que apareca o detalhe todo direto nos detalhes, pode ter
   * opcao ver mais ou estender em que aparece um pequeno resumo»*. PIN E tem razao: o painel do dia
   * ja tem uma dezena de linhas, e enfiar-lhe uma tabela com duas hipoteses, quatro ressalvas e o
   * contexto fazia com que **nada** se lesse. Um resumo de uma linha e uma saida — o que o painel
   * faz com o «Detalhe» do pedido, e com o proprio painel, que abre a pedido.
   */
  const [aberto, definirAberto] = useState(false);

  useEffect(() => {
    const abortar = new AbortController();
    definirD(null);
    definirErro(null);
    // ⚠️ Fecha ao mudar de dia: aberto de um dia para o outro, mostrava o detalhe de um intervalo e
    //    os numeros de outro durante o carregamento.
    definirAberto(false);
    fetch(`/api/plano/intervalo?viatura=${encodeURIComponent(viaturaId)}&dia=${dia}`, {
      signal: abortar.signal,
    })
      .then(async (r) => {
        const c = (await r.json()) as Resposta & { detalhe?: string };
        if (!r.ok) {
          definirErro(c.detalhe ?? `o servidor respondeu ${r.status}`);
          return;
        }
        definirD(c);
      })
      .catch(() => undefined);
    return () => abortar.abort();
  }, [viaturaId, dia]);

  if (erro !== null) return <p className="recomenda-erro">⚠️ {erro}</p>;
  if (d === null) return <p className="recomenda-espera">a calcular o intervalo…</p>;

  const eur = (n: number): string => `${n.toLocaleString('pt-PT')} €`;
  const primeira = d.decisoes[0];
  const onde = d.localidade === null ? 'sítio desconhecido' : d.localidade;

  // ⚠️⚠️ **O RESUMO EM UMA LINHA: dias, onde, e a recomendacao com a margem.** 📌 E o suficiente para
  //    decidir se vale a pena abrir — e a margem e o que diz se a decisao e apertada ou obvia.
  // ⚠️⚠️ **O FUTURO VAZIO: sem viagem marcada NAO SE CONTAM DIAS.** 📗 Cliente, 2026-08-11: *«a
  //    lista vai estar livre no futuro, coisa que nao acontece agora»*.
  //
  //    Sem servico seguinte o intervalo estende-se ate 31 de Dezembro, e `noites` passava a ser
  //    «340 dias parado» — que **nao e falso e e inutil**: nao mede uma paragem, mede o resto do
  //    ano civil. E em producao esse e o caso NORMAL, nao a excepcao: a folha comeca vazia.
  //
  //    ⚠️ O numero nao se corrige, **retira-se**: nao ha resposta certa para «quantos dias vai
  //    estar parada» quando ninguem marcou a viagem seguinte. Diz-se desde quando, que e o unico
  //    facto que existe — a mesma disciplina de D-087, distinguir prova de ignorancia.
  const semViagemMarcada = d.servicoSeguinte === null;
  const desde = `parado desde ${nomeDoDia(d.dia0)}, sem viagem marcada`;
  const resumo = semViagemMarcada
    ? `${onde} · ${desde}`
    : primeira === undefined
      ? `${d.noites} dia(s) parado em ${onde}`
      : `${d.noites} dia(s) em ${onde} · ${primeira.recomendado === 'a_espera' ? 'ficar' : 'ir a casa'}` +
        ` poupa ${eur(primeira.comparacao.margem)}`;

  /**
   * ⚠️⚠️ **O QUE JÁ FOI REGISTADO SOBRE ESTE DIA, e aparece SEMPRE — não atrás do «abrir».**
   *
   * 📗 Cliente, 2026-08-12: *«eu mesmo pondo que o motorista vai a casa… continua a aparecer
   * pernoita fora da base»*. 📌 Uma escrita que só se vê depois de abrir uma secção é, para quem
   * acabou de gravar, indistinguível de uma escrita que não aconteceu — e é exactamente a família
   * de defeito que já custou nove caminhos de escrita a este sistema.
   *
   * ⚠️ **Diz-se «registado» de propósito:** o que está aqui é uma decisão de uma pessoa, não uma
   * conta nossa. A recomendação continua ao lado, e as duas podem discordar — é o planeador que
   * manda, e ver a discordância é o que lhe permite mudar de ideias.
   */
  const registado = d.pernoitaRegistada ?? [];

  return (
    <section className="recomenda">
      {/* ⚠️⚠️ **O RESUMO SEMPRE, O DETALHE A PEDIDO.** 📗 Cliente, 2026-08-11: *«nao quero que
          apareca o detalhe todo direto nos detalhes, pode ter opcao ver mais ou estender em que
          aparece um pequeno resumo»*. 📌 O painel do dia ja tem uma dezena de linhas; enfiar-lhe uma
          tabela com duas hipoteses, quatro ressalvas e o contexto fazia com que **nada** se lesse. */}
      <button
        type="button"
        className="recomenda-cabeca"
        onClick={() => definirAberto((a) => !a)}
        aria-expanded={aberto}
      >
        <span className="recomenda-marca">intervalo</span>
        <span className="recomenda-resumo">{resumo}</span>
        {/* ⚠️ A troca a meio aparece JA no resumo: muda de quem a conta esta a falar, e quem le tem de
            o saber antes de acreditar no numero. */}
        {d.trocaAMeio > 0 ? (
          <span className="recomenda-troca">+{d.trocaAMeio} trocado(s) a meio</span>
        ) : null}
        <span className="recomenda-ver">{aberto ? 'ver menos' : 'ver mais'}</span>
      </button>

      {/* ⚠️⚠️ **O QUE JÁ FOI REGISTADO SOBRE ESTE DIA — fora do botão, e sempre visível.**
          📗 Cliente, 2026-08-12: *«eu mesmo pondo que o motorista vai a casa… continua a aparecer
          pernoita fora da base, alojamento pago»*. 📌 A decisão vivia na linha do motorista
          (`viatura_id NULL`, D-087) e o painel é uma vista de viatura-dia: **não havia junção**, e a
          escrita era invisível por construção. Fica **fora** do `<button>` de propósito — um facto
          registado não pode estar atrás de um clique que ninguém sabe que tem de dar. */}
      {registado.length > 0 ? (
        <p className="recomenda-registado">
          {registado.map((r) => (
            <span key={r.motoristaId}>
              <strong>{r.nome}</strong>:{' '}
              {r.tipoDia === 'em_casa'
                ? `em casa${r.localidade === null ? '' : ` (${nomeLocalidade(r.localidade)})`} — sem alojamento a pagar`
                : `fica no local${r.localidade === null ? '' : ` (${nomeLocalidade(r.localidade)})`} — alojamento pago`}
              , <em>registado</em>.{' '}
            </span>
          ))}
        </p>
      ) : null}

      {aberto ? (
        <div className="recomenda-corpo">
          {/* ⚠️⚠️ **O CONTEXTO PRIMEIRO, e e o que o cliente pediu: «dependendo do contexto, proxima
              viagem etc.».** 📌 Sem saber quando e onde e a viagem seguinte, a recomendacao e um
              numero sem chao. E o intervalo conta-se **entre serviços** e nao pelos dias com registo —
              foi o defeito «parado 7 dias quando eram 10». */}
          <dl className="recomenda-contexto">
            <dt>Dias parados</dt>
            <dd>
              {/* ⚠️ Sem viagem marcada, `dia1` e 31 de Dezembro e nao o fim de uma paragem: a
                     contagem seria o resto do ano civil disfarcado de medicao. */}
              {semViagemMarcada ? (
                <>desde {nomeDoDia(d.dia0)} — sem viagem marcada a seguir</>
              ) : (
                <>
                  {d.noites} ({nomeDoDia(d.dia0)} a {nomeDoDia(d.dia1)})
                </>
              )}
            </dd>
            <dt>Onde</dt>
            <dd>{d.localidade === null ? 'não se sabe' : nomeLocalidade(d.localidade)}</dd>
            <dt>Vem de</dt>
            <dd>
              {d.servicoAnterior === null
                ? '— (é o início do ano no plano)'
                : `${d.servicoAnterior.referencia ?? 'sem referência'}, até ${nomeDoDia(
                    d.servicoAnterior.dia,
                  )}`}
            </dd>
            <dt>Vai para</dt>
            <dd>
              {d.servicoSeguinte === null ? (
                // ⚠️ **Sem viagem seguinte, «esperar» nao e hipotese** — nao se espera por nada, e e
                //    a diferenca que muda a recomendacao.
                <>— nenhuma viagem a seguir neste plano</>
              ) : (
                <>
                  {d.servicoSeguinte.referencia ?? 'sem referência'} a{' '}
                  {nomeDoDia(d.servicoSeguinte.dia)}
                  {d.servicoSeguinte.localidade === null
                    ? ''
                    : `, em ${nomeLocalidade(d.servicoSeguinte.localidade)}`}
                  {d.servicoSeguinte.localidade !== null &&
                  d.servicoSeguinte.localidade === d.localidade
                    ? ' — no MESMO local'
                    : ' — noutro local'}
                </>
              )}
            </dd>
          </dl>

          {/* ⚠️⚠️ **A TROCA A MEIO explica-se, e nao so se conta.** 📌 Medido na base: 26,9 % dos
              serviços tem troca a meio, e em 26,2 % os condutores do serviço inteiro diferem dos do
              ultimo dia. A conta fala de quem estava LA no fim; os outros tem intervalos proprios, no
              dia e no sitio da troca, que uma vista por viatura-dia nao mostra. */}
          {d.trocaAMeio > 0 ? (
            <p className="recomenda-troca-nota">
              ⚠️ Houve <strong>troca de condutor a meio</strong> deste serviço:{' '}
              {d.trocaAMeio} condutor(es) passaram por ele e <strong>não estavam no último dia</strong>.
              A conta abaixo é sobre quem lá ficou. Os outros têm intervalos próprios, a começar no dia
              e no local da troca — e esta vista, que é por viatura, não os mostra: estão em
              Consultas › Sem regressar à base › «intervalos de».
            </p>
          ) : null}

          {d.semConta !== null ? (
            <p className="recomenda-sem">⚠️ Sem recomendação: {d.semConta}.</p>
          ) : null}

          {primeira === undefined ? null : (
            <>
              <p className="recomenda-titulo">
                Para {d.condutores.map((c) => c.nome).join(', ')} — o que a conta diz:
              </p>
              <table className="recomenda-tabela">
                <tbody>
                  <tr data-vence={primeira.recomendado === 'a_espera'}>
                    <th>Ficar onde está</th>
                    <td>{eur(primeira.comparacao.esperar.custo)}</td>
                    <td>
                      {primeira.noites} noite(s) de alojamento
                      {d.pais === null ? '' : ` (${d.pais})`}
                    </td>
                  </tr>
                  <tr data-vence={primeira.recomendado === 'em_casa'}>
                    <th>Ir a casa e voltar</th>
                    <td>{eur(primeira.comparacao.irACasa.custo)}</td>
                    <td>ida e volta por {primeira.meio} (meio inferido da distância)</td>
                  </tr>
                </tbody>
              </table>
              <p className="recomenda-resultado">
                {primeira.recomendado === null
                  ? 'as duas custam o mesmo'
                  : primeira.recomendado === 'a_espera'
                    ? `recomendado: FICAR — poupa ${eur(primeira.comparacao.margem)}`
                    : `recomendado: IR A CASA — poupa ${eur(primeira.comparacao.margem)}`}
                {primeira.comparacao.confianca === 'palpite' ? (
                  <>
                    {' '}
                    — <strong>com preços por medir</strong> (alojamento e deslocação do motorista)
                  </>
                ) : null}
              </p>

              {/* ⚠️⚠️ **AS RESSALVAS AO LADO DA RECOMENDACAO, sempre.** 📌 Dois dos quatro factores
                  que o cliente nomeou nao tem preco: os dias seguidos ja viajados (art. 8.o) e as
                  viagens por perto. A conta nao os soma; se nao os disser, o euro fica com a ultima
                  palavra numa decisao que nao e so de euros. */}
              <ul className="recomenda-ressalvas">
                {primeira.comparacao.ressalvas.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>

              {/* ⚠️⚠️ Sem botao de aplicar. Escrever nos dias de um motorista com base em dois precos
                  inventados era a coisa mais perigosa deste ecra. */}
              <p className="recomenda-nota">
                Isto é uma recomendação, não uma alteração: nada foi gravado. Quem decide é quem
                assina.
              </p>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
